import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const questions = [
  { id: 'q1_difficult_around_teammates', text: '1. It was difficult to be around teammates' },
  { id: 'q2_difficult_to_do_needed', text: '2. I found it difficult to do what I needed to do' },
  { id: 'q3_less_motivated', text: '3. I was less motivated' },
  { id: 'q4_irritable_angry_aggressive', text: '4. I was irritable, angry or aggressive' },
  { id: 'q5_worrying_injury_performance', text: '5. I could not stop worrying about injury or my performance' },
  { id: 'q6_training_stressful', text: '6. I found training more stressful' },
  { id: 'q7_selection_pressure', text: '7. I found it hard to cope with selection pressures' },
  { id: 'q8_worried_life_after_sport', text: '8. I worried about life after sport' },
  { id: 'q9_substance_to_relax', text: '9. I needed alcohol or other substances to relax' },
  { id: 'q10_unusual_risks', text: '10. I took unusual risks off-field' }
];

const scaleOptions = [
  { value: 1, label: 'None of the time' },
  { value: 2, label: 'A little of the time' },
  { value: 3, label: 'Some of the time' },
  { value: 4, label: 'Most of the time' },
  { value: 5, label: 'All of the time' }
];

export default function SMHATAssessment() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [formData, setFormData] = useState({
    assessment_date: new Date().toISOString().split('T')[0],
    q1_difficult_around_teammates: 1,
    q2_difficult_to_do_needed: 1,
    q3_less_motivated: 1,
    q4_irritable_angry_aggressive: 1,
    q5_worrying_injury_performance: 1,
    q6_training_stressful: 1,
    q7_selection_pressure: 1,
    q8_worried_life_after_sport: 1,
    q9_substance_to_relax: 1,
    q10_unusual_risks: 1,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadChild = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}`);
      setChild(response.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    }
  }, [childId]);

  useEffect(() => {
    loadChild();
  }, [loadChild]);

  const calculateTotal = () => {
    return Object.keys(formData)
      .filter(key => key.startsWith('q'))
      .reduce((sum, key) => sum + parseInt(formData[key]), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        assessment_date: new Date(formData.assessment_date).toISOString(),
        q1_difficult_around_teammates: parseInt(formData.q1_difficult_around_teammates),
        q2_difficult_to_do_needed: parseInt(formData.q2_difficult_to_do_needed),
        q3_less_motivated: parseInt(formData.q3_less_motivated),
        q4_irritable_angry_aggressive: parseInt(formData.q4_irritable_angry_aggressive),
        q5_worrying_injury_performance: parseInt(formData.q5_worrying_injury_performance),
        q6_training_stressful: parseInt(formData.q6_training_stressful),
        q7_selection_pressure: parseInt(formData.q7_selection_pressure),
        q8_worried_life_after_sport: parseInt(formData.q8_worried_life_after_sport),
        q9_substance_to_relax: parseInt(formData.q9_substance_to_relax),
        q10_unusual_risks: parseInt(formData.q10_unusual_risks)
      };

      const response = await axios.post(`${API}/children/${childId}/smhat-assessments`, payload);
      
      // Show appropriate message based on score
      if (response.data.exceeds_threshold) {
        toast.warning('Assessment complete. Score exceeds anxiety threshold. Please review recommendations.');
      } else {
        toast.success('Assessment completed successfully!');
      }
      
      navigate(`/child/${childId}`);
    } catch (error) {
      toast.error('Failed to save assessment');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const totalScore = calculateTotal();
  const exceedsThreshold = totalScore >= 10;

  if (!child) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-gray-400 hover:text-white mb-4 rounded-lg"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile
          </Button>
          <h1 className="text-4xl font-bold text-white" data-testid="smhat-title">
            Mental Health Self-Assessment
          </h1>
          <p className="text-gray-400 mt-2">
            IOC Sport Mental Health Assessment Tool 1 (SMHAT-1) - Athlete's Form
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Information Banner */}
        <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <Info className="h-6 w-6 text-[#02b5ff] flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-white mb-2">About This Assessment</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                This is the <strong>International Olympic Committee Sport Mental Health Assessment Tool 1</strong>. 
                It helps identify if you may be experiencing anxiety or distress related to your sport participation.
              </p>
              <p className="text-sm text-gray-300 leading-relaxed">
                <strong>Scoring:</strong> A total score of <span className="text-[#ff025f] font-bold">10 or more</span> exceeds 
                the threshold for anxiety and may indicate the need to speak with a mental health professional or sports psychologist.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Assessment Date */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <Label className="text-sm font-semibold text-gray-300 mb-2 block">
              Assessment Date
            </Label>
            <input
              type="date"
              value={formData.assessment_date}
              onChange={(e) => setFormData({ ...formData, assessment_date: e.target.value })}
              className="h-12 w-full px-4 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
            />
          </div>

          {/* Instructions */}
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <h2 className="text-xl font-bold mb-2">Instructions</h2>
            <p className="text-purple-100 text-sm leading-relaxed">
              These questions concern how you have been feeling <strong>over the past 30 days</strong>. 
              Please select the answer that best represents how you have been feeling.
            </p>
          </div>

          {/* Questions */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <div className="space-y-8">
              {questions.map((question, index) => (
                <div key={question.id} className="pb-6 border-b border-zinc-800 last:border-0">
                  <Label className="text-base font-semibold text-white mb-4 block">
                    {question.text}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    {scaleOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, [question.id]: option.value })}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          formData[question.id] === option.value
                            ? 'border-[#02b5ff] bg-purple-50 shadow-md'
                            : 'border-zinc-700 hover:border-gray-300 hover:bg-zinc-800'
                        }`}
                      >
                        <div className={`text-2xl font-bold mb-1 ${
                          formData[question.id] === option.value ? 'text-[#02b5ff]' : 'text-gray-400'
                        }`}>
                          {option.value}
                        </div>
                        <div className={`text-xs ${
                          formData[question.id] === option.value ? 'text-[#02b5ff]' : 'text-gray-400'
                        }`}>
                          {option.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Score Display */}
          <div className={`rounded-2xl p-6 border-2 ${
            exceedsThreshold 
              ? 'bg-[#ff025f]/10 border-[#ff025f]/30' 
              : 'bg-[#02b5ff]/10 border-[#02b5ff]/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {exceedsThreshold ? (
                  <AlertTriangle className="h-8 w-8 text-[#ff025f]" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-[#02b5ff]" />
                )}
                <div>
                  <div className="text-sm font-semibold text-gray-300">Current Total Score</div>
                  <div className={`text-4xl font-bold ${
                    exceedsThreshold ? 'text-[#ff025f]' : 'text-[#02b5ff]'
                  }`}>
                    {totalScore} / 50
                  </div>
                </div>
              </div>
              <div className={`text-right ${exceedsThreshold ? 'text-[#ff025f]' : 'text-[#02b5ff]'}`}>
                {exceedsThreshold ? (
                  <div>
                    <div className="text-sm font-bold mb-1">Exceeds Threshold</div>
                    <div className="text-xs">Consider professional support</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-bold mb-1">Within Normal Range</div>
                    <div className="text-xs">Continue monitoring</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-zinc-900 rounded-2xl shadow-lg p-8 border border-zinc-800">
            <Label className="text-sm font-semibold text-gray-300 mb-2 block">
              Additional Notes (Optional)
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="min-h-24 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-purple-500 text-white rounded-xl"
              placeholder="Any additional information about your wellbeing or specific concerns..."
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate(`/child/${childId}`)}
              className="h-12 px-8 bg-gray-100 hover:bg-gray-200 text-gray-300 font-semibold rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="h-12 px-8 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold rounded-full shadow-lg flex-1"
            >
              <Save className="mr-2 h-5 w-5" />
              {submitting ? 'Submitting...' : 'Complete Assessment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
